import React from 'react';
import { throughputUnitLabelFromThroughputControlType } from 'hooks/useDisplayUnits/display';
import {
  BasicDisplayUnits,
  UnitBase,
  convertIfRequired,
  getAbbreviation,
  getPressurePumpUnitLabel,
  unitLabels
} from 'hooks/useDisplayUnits/useDisplayUnits';
import ProjectContext from 'modules/project/providers/project/context';
import {
  CustomUnitFields,
  MetricPressureUnit,
  ProjectDto
} from 'providers/api';
import { createProjectNumberFormatter } from 'utils';
import { isCustomUnitField, pumpPressureBarToKpa, pumpPressureKpaToBar } from 'utils/unitConversions';

export interface PumpPressureUnits {
  unit: UnitBase;
  // Bar to and from the metric sub-unit (bar or kPa); the unit's own conversions take care of psi.
  toDisplay: (bar: number) => number;
  fromDisplay: (value: number) => number;
}

/**
 * kPa only applies while pump pressure is actually shown in metric. A project switched to imperial keeps its
 * MetricPressureUnit, and the kPa unit's conversion reads kPa, not psi. See .claude/features/admin/PRESSURE_AND_VOLUME_UNITS.md.
 */
export const pumpPressureUnits = (project: ProjectDto): PumpPressureUnits => {
  const showsKiloPascal = project.metricPressureUnit === MetricPressureUnit.KiloPascal
    && !isCustomUnitField(project.unitSystemPreference, project.customUnitFields ?? [], CustomUnitFields.PumpPressure);
  return showsKiloPascal
    ? { unit: getPressurePumpUnitLabel(MetricPressureUnit.KiloPascal), toDisplay: pumpPressureBarToKpa, fromDisplay: pumpPressureKpaToBar }
    : { unit: unitLabels[BasicDisplayUnits.PressurePump], toDisplay: (bar) => bar, fromDisplay: (value) => value };
};

export interface AssignerUnits {
  pumpPressure: PumpPressureUnits;
  formatPumpPressureKpa: (kpa: number) => string;
  formatLength: (metres: number) => string;
  formatThroughput: (throughput: number) => string;
  formatNumber: (value: number, decimalPlaces: number) => string;
}

const useAssignerUnits = (project: ProjectDto): AssignerUnits => {
  const projectContext = React.useContext(ProjectContext);
  const formatNumber = createProjectNumberFormatter(projectContext?.state.decimalSeparator, projectContext?.state.thousandsSeparator);
  const { unitSystemPreference } = project;
  const customUnitFields = project.customUnitFields ?? [];
  const pumpPressure = pumpPressureUnits(project);

  const withUnit = (unit: UnitBase, value: number) => {
    const converted = convertIfRequired(value, unitSystemPreference, unit, customUnitFields);
    return `${formatNumber(converted, unit.decimalPlaces)} ${getAbbreviation(unit, unitSystemPreference, customUnitFields)}`;
  };

  return {
    pumpPressure,
    formatPumpPressureKpa: (kpa: number) => withUnit(pumpPressure.unit, pumpPressure.toDisplay(pumpPressureKpaToBar(kpa))),
    formatLength: (metres: number) => withUnit(unitLabels[BasicDisplayUnits.Distance], metres),
    formatThroughput: (throughput: number) => withUnit(throughputUnitLabelFromThroughputControlType(project.throughput), throughput),
    formatNumber,
  };
};

export default useAssignerUnits;
